/* eslint-disable radix */
import { isEmpty } from 'lodash';
import { createSelector } from 'reselect';
import type { Address, Customer } from '@app/types';
import { getSelectedCurrency } from '@app/redux/currency/selectors';
import { getSelectedRegion } from '@app/redux/localization/selectors';
import { INITIAL_STATE } from './reducers';

export const getCustomer = ({
  customer = INITIAL_STATE,
}: {
  customer?: Partial<Customer>;
}) => (!isEmpty(customer) ? customer : {});
export const getCustomerInternal = ({
  customer = {},
}: {
  customer?: Partial<Customer>;
}) => customer;

export const getCustomerId = createSelector(
  getCustomerInternal,
  ({ id = '' }) => id,
);
export const getCustomerUUID = createSelector(
  getCustomerInternal,
  ({ uuid = '' }) => uuid,
);
export const getCustomerEmail = createSelector(
  getCustomerInternal,
  ({ email = '' }) => email,
);
export const getTeamMember = createSelector(
  getCustomerInternal,
  ({ teamMember = false }) => teamMember,
);

export const getCustomerShippingAddress = createSelector(
  getCustomerInternal,
  ({ Shipping = {} }) => Shipping.Address || {},
);
export const getCustomerBillingAddress = createSelector(
  getCustomerInternal,
  ({ Billing = {} }) => Billing?.Address || ({} as Partial<Address>),
);
export const getCustomerShippingCountry = createSelector(
  getCustomerShippingAddress,
  ({ countryCodeAlpha2 = 'US' }: { countryCodeAlpha2?: string }) =>
    countryCodeAlpha2,
);
export const getCustomerBillingRegion = createSelector(
  getCustomerBillingAddress,
  ({ region = '' }) => region,
);
export const getCustomerBillingCountry = createSelector(
  getCustomerBillingAddress,
  ({ countryCodeAlpha2 = 'US' }) => countryCodeAlpha2,
);
export const getLoginStatus = createSelector(
  getCustomer,
  (customer) => !isEmpty(customer),
);

export const getCustomerGDPRStatus = createSelector(
  getCustomerInternal,
  ({ gdprStatus = null }) => gdprStatus,
);

export const getCcic = createSelector(
  getCustomerInternal,
  ({ Meta = { ccicId: null } }) => (!Meta.ccicId ? null : Meta.ccicId),
);

export const getQatarId = createSelector(
  getCustomerInternal,
  ({ Meta = { qatarId: null } }) => (!Meta.qatarId ? null : Meta.qatarId),
);

export const getIsOnVacation = createSelector(getCustomerInternal, (customer) =>
  Boolean(customer?.vacationDate),
);

export const getShipByDate = createSelector(
  getCustomerInternal,
  ({ shipByDate }) => shipByDate,
);

export const getIsEmployee = createSelector(
  getCustomer,
  ({ teamMember }) => teamMember,
);

// Used by Segment
// Passed to identify
// Passed as options -> traits to trackScreen, trackEvent
export const getCustomerTraits = createSelector(
  getCustomer,
  getCustomerBillingCountry,
  getCustomerShippingCountry,
  getSelectedCurrency,
  (customer, billingCountry, shippingCountry, currency) => ({
    billingCountry,
    createdAt: customer?.createdAt,
    currency,
    email: customer?.email,
    firstName: customer?.firstName,
    lastName: customer?.lastName,
    shippingCountry,
    shoeSize: customer?.defaultSize,
    userName: customer?.username,
    userUUID: customer?.uuid,
    sha256Email: customer?.sha256Email,
  }),
);

export const getCustomerMarketCountry = createSelector(
  getCustomerInternal,
  getSelectedRegion,
  ({ marketCountry }, selectedRegion) => marketCountry || selectedRegion,
);

export const getCustomerFullName = createSelector(
  getCustomer,
  ({ fullName }) => fullName,
);

export const getCustomerLanguage = createSelector(
  getCustomer,
  ({ language }) => language,
);

export const getCustomerDefaultSize = createSelector(
  getCustomer,
  ({ defaultSize }) => defaultSize,
);

export const getCustomerMarket = createSelector(
  getCustomer,
  ({ marketName }) => marketName,
);

export const getIsStockXTrader = createSelector(
  getCustomer,
  ({ id }) => parseInt(id || '') === 802,
);
